/**
 * PKCE (RFC 7636) + state helpers for the OIDC authorization-code flow.
 *
 * The SPA is a public client, so it cannot hold a client secret. PKCE protects
 * the code exchange: we send a random `code_verifier`'s SHA-256 challenge on the
 * authorize request and the raw verifier on the token exchange (server-side via
 * our BFF). `state` is an unguessable value echoed back to defend against CSRF.
 *
 * Verifier + state are stashed per-tab in sessionStorage between the redirect to
 * the Cognito hosted UI and the callback (one login per tab — same rationale as
 * post-login-redirect.ts).
 */

const VERIFIER_KEY = 'pkce_code_verifier'
const STATE_KEY = 'pkce_state'

/** base64url-encode bytes without padding (RFC 7636 §A). */
function base64UrlEncode(bytes: ArrayBuffer): string {
	const binary = String.fromCharCode(...new Uint8Array(bytes))
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** A cryptographically-random URL-safe string of `byteLength` bytes. */
function randomUrlSafe(byteLength: number): string {
	const bytes = new Uint8Array(byteLength)
	crypto.getRandomValues(bytes)
	return base64UrlEncode(bytes.buffer)
}

/** Generate a PKCE code verifier (43–128 chars; 32 bytes → 43 base64url chars). */
export function generateCodeVerifier(): string {
	return randomUrlSafe(32)
}

/** Derive the S256 code challenge from a verifier. */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
	return base64UrlEncode(digest)
}

/** Generate an opaque CSRF `state` value. */
export function generateState(): string {
	return randomUrlSafe(16)
}

/** Persist verifier + state for the in-flight login. */
export function stashPkce(verifier: string, state: string): void {
	sessionStorage.setItem(VERIFIER_KEY, verifier)
	sessionStorage.setItem(STATE_KEY, state)
}

/** Read + clear the stashed verifier and state (consumed at the callback). */
export function consumePkce(): { verifier: string | null; state: string | null } {
	const verifier = sessionStorage.getItem(VERIFIER_KEY)
	const state = sessionStorage.getItem(STATE_KEY)
	sessionStorage.removeItem(VERIFIER_KEY)
	sessionStorage.removeItem(STATE_KEY)
	return { verifier, state }
}
